import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import { siteConfig } from '@/config/site';
import {
    BarChart3,
    Bot,
    Users,
    Smartphone,
    Layers,
    Zap,
    Monitor,
    Globe,
    ArrowRight,
} from 'lucide-react';

const features = [
    {
        icon: BarChart3,
        title: 'Advanced Charting',
        description:
            'Professional-grade charting with 70+ indicators, multiple timeframes, and drawing tools for in-depth technical analysis.',
    },
    {
        icon: Bot,
        title: 'Algorithmic Trading',
        description:
            `Build, backtest, and deploy automated trading strategies in ${siteConfig.platform.automation.name} using the built-in MetaEditor.`,
    },
    {
        icon: Users,
        title: 'Copy Trading',
        description:
            'Follow and copy top-performing traders or become a strategy provider and earn commissions from your followers.',
    },
    {
        icon: Smartphone,
        title: 'Mobile Access',
        description:
            'Trade on the go with full-featured mobile apps for iOS and Android, including real-time charts and order management.',
    },
    {
        icon: Layers,
        title: 'Level II Pricing',
        description:
            'Access full market depth with Level II pricing, giving you visibility into the order book and liquidity at every price level.',
    },
    {
        icon: Zap,
        title: 'Fast Execution',
        description:
            'Ultra-low latency order execution with no dealing desk intervention. Your trades are filled at the best available price.',
    },
];

const platforms = [
    {
        icon: Globe,
        title: `${siteConfig.platform.shortName} Web`,
        description:
            'Trade directly from your browser with no downloads required. Full charting, real-time data, and seamless account management.',
        href: '/platform/web',
    },
    {
        icon: Monitor,
        title: `${siteConfig.platform.shortName} Desktop`,
        description:
            'The full-powered desktop application with advanced charting, algorithmic trading, custom indicators, and multi-monitor support.',
        href: '/platform/desktop',
    },
    {
        icon: Smartphone,
        title: `${siteConfig.platform.shortName} Mobile`,
        description:
            'Trade on the go with push notifications, full order management, and professional charting on iOS and Android devices.',
        href: '/platform/mobile',
    },
];

export default function Overview() {
    return (
        <AppLayout title={`Platform Overview - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="bg-background pt-32 lg:pt-40 pb-20">
                <div className="text-center max-w-4xl mx-auto">
                    <FadeIn>
                        <Badge className="mb-6">Trading Platform</Badge>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            {siteConfig.platform.name} <span className="text-gradient">Platform</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.2}>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-10">
                            Institutional-Grade Trading Technology
                        </p>
                    </FadeIn>
                    <FadeIn delay={0.3}>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg">
                                    Get Started
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </a>
                            <Link to="/funding/overview">
                                <Button size="lg" variant="outline">
                                    View Programs
                                </Button>
                            </Link>
                        </div>
                    </FadeIn>
                </div>
            </Section>

            {/* Features Grid */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Platform"
                        highlight="Features"
                        description={`Everything you need to trade at the highest level, powered by ${siteConfig.platform.name}'s institutional-grade technology.`}
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature) => {
                            const Icon = feature.icon;
                            return (
                                <motion.div key={feature.title} variants={staggerItem}>
                                    <Card className="h-full rounded-xl border border-border bg-card/80 backdrop-blur-sm transition-colors hover:border-primary/20">
                                        <CardContent className="p-6 lg:p-8">
                                            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                                <Icon className="h-5 w-5 text-primary" />
                                            </div>
                                            <h3 className="mb-2 text-lg font-semibold text-foreground">
                                                {feature.title}
                                            </h3>
                                            <p className="text-sm leading-relaxed text-muted-foreground">
                                                {feature.description}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </StaggerChildren>
            </Section>

            {/* Platform Options */}
            <Section className="bg-background">
                <FadeIn>
                    <SectionHeader
                        title="Choose Your"
                        highlight="Platform"
                        description={`Access ${siteConfig.platform.name} on web, desktop, or mobile -- trade your way, anywhere.`}
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {platforms.map((platform) => {
                            const Icon = platform.icon;
                            return (
                                <motion.div key={platform.title} variants={staggerItem}>
                                    <Card className="h-full rounded-xl border border-border bg-card/80 backdrop-blur-sm text-center transition-colors hover:border-primary/20">
                                        <CardContent className="p-8">
                                            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                                <Icon className="h-7 w-7 text-primary" />
                                            </div>
                                            <h3 className="text-xl font-semibold text-foreground mb-3">
                                                {platform.title}
                                            </h3>
                                            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                                                {platform.description}
                                            </p>
                                            <Link to={platform.href}>
                                                <Button variant="outline" className="w-full">
                                                    Learn More
                                                    <ArrowRight className="ml-2 h-4 w-4" />
                                                </Button>
                                            </Link>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </StaggerChildren>
            </Section>

            {/* CTA */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            Start Trading on <span className="text-gradient">{siteConfig.platform.name}</span>
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8">
                            Experience institutional-grade trading technology. Get funded and trade with the best platform in the industry.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg">
                                    Get Started
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </a>
                            <Link to="/funding/overview">
                                <Button size="lg" variant="outline">
                                    View Programs
                                </Button>
                            </Link>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
